import { PieChart, Pie, Cell, ResponsiveContainer } from "recharts";
import { ExternalLink } from "lucide-react";
import { useState, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { springs } from "@/components/ui/motion";
import { useTranslation } from "@/packages/utils";
import type { PipelineStage } from "./dashboard.types";

function PipelineLegend({
  compact,
  activeStages,
  toggleStage,
  stages,
}: {
  compact: boolean;
  activeStages: Set<string>;
  toggleStage: (status: string) => void;
  stages: PipelineStage[];
}) {
  return (
    <div
      className={
        compact
          ? "flex flex-col gap-1"
          : "flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-2 border-t border-border"
      }
    >
      {stages.map((stage) => (
        <button
          key={stage.status}
          type="button"
          onClick={() => toggleStage(stage.status)}
          className="flex items-center gap-1.5 cursor-pointer hover:opacity-80 transition-opacity outline-none"
        >
          <span
            className="w-2.5 h-2.5 rounded-full shrink-0"
            style={{ backgroundColor: stage.color }}
          />
          <span
            className={`text-xs whitespace-nowrap transition-colors ${
              activeStages.has(stage.status)
                ? "text-primary font-semibold"
                : "text-secondary"
            }`}
          >
            {stage.status} ({stage.count})
          </span>
        </button>
      ))}
    </div>
  );
}

// ── Skeleton ─────────────────────────────────────────────────────

function PipelineSkeleton() {
  return (
    <div className="flex-1 flex items-center justify-center px-4 pb-3 animate-pulse">
      <div className="w-[160px] h-[160px] rounded-full bg-card-hover" />
    </div>
  );
}

// ── Ghost empty state ────────────────────────────────────────────

const GHOST_SEGMENTS = [
  { label: "Ausstehend", color: "#a3e635", pct: 30 },
  { label: "In Bearbeitung", color: "#c084fc", pct: 25 },
  { label: "Eingereicht", color: "#22d3ee", pct: 20 },
  { label: "Genehmigt", color: "#34d399", pct: 15 },
  { label: "Abgelehnt", color: "#f87171", pct: 10 },
];

function PipelineGhost() {
  // Build SVG donut with ghost segments
  const size = 160;
  const cx = size / 2;
  const cy = size / 2;
  const outer = 68;
  const inner = 44;

  let cumulative = 0;
  const arcs = GHOST_SEGMENTS.map((seg) => {
    const startAngle = (cumulative / 100) * 360 - 90;
    cumulative += seg.pct;
    const endAngle = (cumulative / 100) * 360 - 90;
    const startRad = (startAngle * Math.PI) / 180;
    const endRad = (endAngle * Math.PI) / 180;
    const largeArc = seg.pct > 50 ? 1 : 0;

    const x1o = cx + outer * Math.cos(startRad);
    const y1o = cy + outer * Math.sin(startRad);
    const x2o = cx + outer * Math.cos(endRad);
    const y2o = cy + outer * Math.sin(endRad);
    const x1i = cx + inner * Math.cos(endRad);
    const y1i = cy + inner * Math.sin(endRad);
    const x2i = cx + inner * Math.cos(startRad);
    const y2i = cy + inner * Math.sin(startRad);

    const d = [
      `M ${x1o} ${y1o}`,
      `A ${outer} ${outer} 0 ${largeArc} 1 ${x2o} ${y2o}`,
      `L ${x1i} ${y1i}`,
      `A ${inner} ${inner} 0 ${largeArc} 0 ${x2i} ${y2i}`,
      "Z",
    ].join(" ");

    return { d, color: seg.color };
  });

  return (
    <div className="flex-1 flex flex-col items-center justify-between px-4 pb-3 opacity-15">
      {/* Ghost donut */}
      <div className="flex-1 flex items-center justify-center min-h-0">
        <svg
          width={size}
          height={size}
          viewBox={`0 0 ${size} ${size}`}
          role="img"
          aria-label="Empty pipeline chart"
        >
          {arcs.map((arc, i) => (
            <path key={GHOST_SEGMENTS[i].label} d={arc.d} fill={arc.color} />
          ))}
          {/* Center text */}
          <text
            x={cx}
            y={cy - 4}
            textAnchor="middle"
            className="fill-primary text-[24px] font-extrabold"
          >
            0%
          </text>
          <text
            x={cx}
            y={cy + 14}
            textAnchor="middle"
            className="fill-secondary text-[11px] font-medium"
          >
            Compliance
          </text>
        </svg>
      </div>
      {/* Ghost legend */}
      <div className="flex flex-wrap items-center justify-center gap-x-4 gap-y-1 pt-2 border-t border-border w-full">
        {GHOST_SEGMENTS.map((seg) => (
          <div key={seg.label} className="flex items-center gap-1.5">
            <span
              className="w-2.5 h-2.5 rounded-full shrink-0"
              style={{ backgroundColor: seg.color }}
            />
            <span className="text-xs text-secondary whitespace-nowrap">
              {seg.label} (0)
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── AuditPipeline ────────────────────────────────────────────────

interface AuditPipelineProps {
  data: PipelineStage[] | null;
  isLoading: boolean;
  onAuditClick?: (
    auditId: NonNullable<PipelineStage["audits"][number]["id"]>,
  ) => void;
}

export function AuditPipeline({
  data,
  isLoading,
  onAuditClick,
}: AuditPipelineProps) {
  const { t } = useTranslation(["dashboard"]);
  const [activeStages, setActiveStages] = useState<Set<string>>(new Set());

  const pipeline = data ?? [];
  const totalAudits = useMemo(
    () => pipeline.reduce((sum, s) => sum + s.count, 0),
    [pipeline],
  );
  const completionRate = useMemo(() => {
    const completedCount = pipeline.find((s) => s.isCompleted)?.count ?? 0;
    return totalAudits > 0
      ? Math.round((completedCount / totalAudits) * 100)
      : 0;
  }, [pipeline, totalAudits]);
  const chartData = useMemo(
    () =>
      pipeline.map((stage) => ({
        name: stage.status,
        value: stage.count,
        color: stage.color,
      })),
    [pipeline],
  );

  const isCompact = activeStages.size > 0;

  const toggleStage = useCallback((status: string) => {
    setActiveStages((prev) => {
      const next = new Set(prev);
      if (next.has(status)) {
        next.delete(status);
      } else {
        next.add(status);
      }
      return next;
    });
  }, []);

  const handlePieClick = useCallback(
    (_: unknown, index: number) => {
      const stage = pipeline[index];
      if (stage) toggleStage(stage.status);
    },
    [pipeline, toggleStage],
  );

  const activeAuditGroups = pipeline.filter((s) => activeStages.has(s.status));

  return (
    <div className="rounded-xl bg-card border border-border overflow-hidden h-full flex flex-col">
      <div className="flex items-center justify-between px-4 pt-3 pb-1 shrink-0">
        <h3 className="text-sm font-semibold text-primary">
          {t("dashboard:pipeline.title")}
        </h3>
        {!isLoading && totalAudits > 0 && (
          <span className="text-xs text-muted">
            {t("dashboard:pipeline.totalAudits", { count: totalAudits })}
          </span>
        )}
      </div>

      <AnimatePresence mode="wait">
        {isLoading ? (
          <motion.div
            key="skeleton"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex-1"
          >
            <PipelineSkeleton />
          </motion.div>
        ) : totalAudits === 0 ? (
          <motion.div
            key="empty"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="flex-1 flex flex-col"
          >
            <PipelineGhost />
          </motion.div>
        ) : (
          <motion.div
            key="content"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className={`pipeline-body flex-1 min-h-0 px-4 pb-3 ${isCompact ? "overflow-y-auto" : "overflow-y-hidden"}`}
          >
            {/* Default: donut centered above, legend horizontal at bottom */}
            {/* Compact: donut left + legend stacked right */}
            <div
              className={`flex ${
                isCompact
                  ? "flex-row items-center justify-center gap-3"
                  : "flex-col items-center justify-between h-full"
              }`}
            >
              <div
                className={`pipeline-donut-wrap relative transition-all duration-300 ease-in-out shrink-0 ${
                  isCompact ? "w-[130px] h-[130px]" : "w-full flex-1 min-h-0"
                }`}
              >
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={isCompact ? 36 : "55%"}
                      outerRadius={isCompact ? 54 : "85%"}
                      paddingAngle={0}
                      dataKey="value"
                      animationDuration={400}
                      strokeWidth={0}
                      onClick={handlePieClick}
                      style={{ cursor: "pointer" }}
                    >
                      {chartData.map((entry) => (
                        <Cell
                          key={entry.name}
                          fill={entry.color}
                          opacity={
                            activeStages.size === 0 ||
                            activeStages.has(entry.name)
                              ? 1
                              : 0.35
                          }
                        />
                      ))}
                    </Pie>
                  </PieChart>
                </ResponsiveContainer>

                <div className="pipeline-donut-center">
                  <motion.span
                    animate={{ fontSize: isCompact ? "18px" : "24px" }}
                    transition={springs.snappy}
                    className="font-extrabold text-primary tracking-tight leading-none"
                  >
                    {completionRate}%
                  </motion.span>
                  <motion.span
                    animate={{
                      opacity: isCompact ? 0 : 1,
                      height: isCompact ? 0 : "auto",
                      marginTop: isCompact ? 0 : 4,
                    }}
                    transition={springs.snappy}
                    className="text-xs text-secondary font-medium overflow-hidden"
                  >
                    {t("dashboard:pipeline.compliance")}
                  </motion.span>
                </div>
              </div>

              <div
                className={
                  isCompact
                    ? "flex items-center self-center"
                    : "w-full shrink-0"
                }
              >
                <PipelineLegend
                  compact={isCompact}
                  activeStages={activeStages}
                  toggleStage={toggleStage}
                  stages={pipeline}
                />
              </div>
            </div>

            <AnimatePresence>
              {activeAuditGroups.length > 0 && (
                <motion.div
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 8 }}
                  transition={springs.smooth}
                  className="border-t border-border pt-3 mt-3"
                >
                  {activeAuditGroups.map((group) => (
                    <div key={group.status}>
                      <div className="flex items-center gap-1.5 text-xs font-semibold text-secondary pb-1.5">
                        <span
                          className="w-2 h-2 rounded-full"
                          style={{ background: group.color }}
                        />
                        {group.status}
                      </div>
                      <div className="flex flex-col gap-1.5">
                        {group.audits.map((audit) => {
                          const canOpenAudit =
                            audit.id !== undefined &&
                            onAuditClick !== undefined;

                          return (
                            <button
                              key={audit.id ?? audit.name}
                              type="button"
                              onClick={() => {
                                if (audit.id && onAuditClick) {
                                  onAuditClick(audit.id);
                                }
                              }}
                              disabled={!canOpenAudit}
                              className={`py-2 px-3 bg-surface rounded-lg text-left transition-colors ${
                                canOpenAudit
                                  ? "cursor-pointer hover:bg-surface/80"
                                  : "cursor-default"
                              }`}
                            >
                              <div className="flex items-center justify-between gap-2 mb-1">
                                <span className="text-sm font-medium text-primary truncate">
                                  {audit.name}
                                </span>
                              </div>
                              <div className="flex items-center justify-between gap-2">
                                <span className="text-xs text-muted">
                                  {audit.client}
                                </span>
                                <div className="flex items-center gap-3 shrink-0">
                                  {audit.p !== undefined && (
                                    <div className="flex items-center gap-1.5">
                                      <div className="w-14 h-1.5 bg-border rounded-full overflow-hidden">
                                        <div
                                          className="h-full rounded-full bg-blue-500 transition-[width] duration-500"
                                          style={{ width: `${audit.p}%` }}
                                        />
                                      </div>
                                      <span className="text-xs text-secondary">
                                        {audit.p}%
                                      </span>
                                    </div>
                                  )}
                                  <div className="text-xs text-muted flex items-center gap-1">
                                    <ExternalLink size={12} strokeWidth={2} />
                                    {audit.due}
                                  </div>
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
